import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import moment, { Moment } from 'moment-timezone';
import { utcToZonedTime } from 'date-fns-tz';
import { parseISO, format, sub } from 'date-fns';
import { DateRangeProps } from '../DateRange.types';

const newEnd = new Date();
newEnd.setSeconds(0);
newEnd.setMilliseconds(0);

const newStart = sub(newEnd, { days: 7 });

const defStart = newStart.toISOString();
const defEnd = newEnd.toISOString();

export interface DateRangeContextProps {
  hasGoBackDateLimit?: number;
  hasFutureDates?: boolean;
  dateRange: DateRangeProps;
  setDateRange: (range: DateRangeProps) => void;
  isoDateRange: {
    startTime: string;
    endTime: string;
  };
  utcDateRange: {
    startTime: string;
    endTime: string;
  };
  momentDateRange: {
    startTime: Moment;
    endTime: Moment;
    currentTime: Moment;
  };
  utcTZConvertedISO: {
    startTime: string;
    endTime: string;
  };
  timeZone: string;
  rightNow: string;
}

let now: string | Date = new Date();
now.setMilliseconds(0);
now.setSeconds(0);

let defaultStartTime: string | Date = new Date(now);
defaultStartTime.setDate(now.getDate() - 7);

now = now.toISOString();
defaultStartTime = defaultStartTime.toISOString();

export const DateRangeContext = createContext<DateRangeContextProps>({
  dateRange: {
    startTime: newStart,
    endTime: newEnd
  },
  setDateRange: (range: DateRangeProps) => console.log('status not set' + range),
  isoDateRange: {
    startTime: defStart,
    endTime: defEnd
  },
  utcDateRange: {
    startTime: defStart,
    endTime: defEnd
  },
  timeZone: moment.tz.guess() || `UTC`,
  momentDateRange: {
    startTime: moment(newStart),
    endTime: moment(newEnd),
    currentTime: moment()
  },
  utcTZConvertedISO: {
    startTime: defaultStartTime,
    endTime: now
  },
  rightNow: defEnd
});

export const useDateRange = (): DateRangeContextProps =>
  useContext<DateRangeContextProps>(DateRangeContext);

export interface DateRangeProviderProps {
  subtractDaysCount?: number;
  subtractHoursCount?: number;
  timeZone?: string;
  hasFutureDates?: boolean;
  hasGoBackDateLimit?: number;
  frequencyRefresh?: number;
  startTime?: string;
  endTime?: string;
}

interface Props extends DateRangeProviderProps {
  children?: ReactNode;
}

export const DateRangeProvider = ({
  children,
  subtractDaysCount,
  subtractHoursCount,
  timeZone,
  hasGoBackDateLimit,
  hasFutureDates,
  frequencyRefresh,
  startTime,
  endTime
}: Props): JSX.Element => {
  // Ref to track if date changed and we are looking at past data or current data
  const isEndDateInThePast = useRef();
  // Ref for interval, so we don't create new interval each time
  const intervalRef = useRef();

  // check for incoming timezone, if no timezone, the users local timezone will be used
  if (timeZone) moment.tz.setDefault(timeZone);

  // this uses moment to guess the local timeZone as a fallback for a machine not having one
  const localTimeZone = moment.tz.guess();

  // moment object that is being set/manipulated
  const [machineDateRange, setMachineDateRange] = useState<{
    startMoment: Moment;
    endMoment: Moment;
  }>({
    startMoment: subtractHoursCount
      ? moment().subtract(subtractHoursCount, 'hours')
      : moment().subtract(subtractDaysCount || 7, 'days'),
    endMoment: moment()
  });

  // CleaInterval when component unmounts
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [intervalRef]);

  useEffect(() => {
    if (startTime && endTime) {
      setMachineDateRange({
        startMoment: moment(startTime),
        endMoment: moment(endTime)
      });
    }
  }, [startTime, endTime]);

  // Below block is responsible for setting and clearing interval
  // If end time is in the past then we clear interval
  // If end time is current time then we set interval to refresh every 60 seconds
  useEffect(() => {
    if (!frequencyRefresh) return;

    const differenceInMinutes = moment(machineDateRange.endMoment).diff(moment(), 'minutes');
    isEndDateInThePast.current = Math.abs(differenceInMinutes) > 2 ? true : false;

    if (isEndDateInThePast.current) {
      return () => {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      };
    } else {
      intervalRef.current = setInterval(() => {
        const increment = frequencyRefresh ? frequencyRefresh / 1000 : 60;
        const start = machineDateRange.startMoment.add(increment, 'seconds');
        const end = machineDateRange.endMoment.add(increment, 'seconds');
        setMachineDateRange({
          startMoment: start,
          endMoment: end
        });
      }, frequencyRefresh);

      return () => {
        clearInterval(intervalRef.current);
      };
    }
  }, [machineDateRange, frequencyRefresh]);

  // moment object converted to Date() object, this is the object being used by components
  const dateRange = {
    startTime: machineDateRange.startMoment.toDate(),
    endTime: machineDateRange.endMoment.toDate()
  };

  // string conversion of Date() to ISO format, including timezone offset
  const isoDateRange = {
    startTime: machineDateRange.startMoment.format('YYYY-MM-DDTHH:mm:ssZ'),
    endTime: machineDateRange.endMoment.format('YYYY-MM-DDTHH:mm:ssZ')
  };

  const utcDateRange = {
    startTime: moment.utc(dateRange.startTime).format('YYYY-MM-DDTHH:mm:ssZ'),
    endTime: moment.utc(dateRange.endTime).format('YYYY-MM-DDTHH:mm:ssZ')
  };

  // utcTZConvertedISO will take the current start and end time and
  // calculate what that was in UTC time and convert the times to the UTC time.
  // then it returns the format as
  // 2023-05-24T17:32:39  which do does not have the timezone offset included.
  // this example can be found on the Avure machine pressurization api call
  //
  // TODO:  If this is a better way to do this, please implement.
  //
  const utcTZConvertedISO = {
    startTime: format(
      utcToZonedTime(parseISO(isoDateRange.startTime), isoDateRange.startTime.substring(19)),
      "yyyy-MM-dd'T'HH:mm:ss"
    ),
    endTime: format(
      utcToZonedTime(parseISO(isoDateRange.endTime), isoDateRange.endTime.substring(19)),
      "yyyy-MM-dd'T'HH:mm:ss"
    )
  };

  // handlers for setting date range, it takes Date() objects fir startTime and endTime and
  // coverts to moment object, then sets the state hook
  const setDateRange = ({ startTime, endTime }: DateRangeProps) =>
    setMachineDateRange({
      startMoment: moment(startTime),
      endMoment: moment(endTime)
    });

  const momentDateRange = {
    currentTime: moment(),
    startTime: machineDateRange.startMoment,
    endTime: machineDateRange.endMoment
  };

  const rightNow = moment().toDate();

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        isoDateRange,
        setDateRange,
        timeZone: timeZone || localTimeZone,
        utcDateRange,
        momentDateRange,
        rightNow,
        utcTZConvertedISO,
        hasGoBackDateLimit,
        hasFutureDates
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};
